import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

const API_URL = environment.BASE_API_URL + 'GGKEventWebService.asmx/';
const GGK_API_KEY = environment.GGK_API_KEY;

// --- Interfaces ---

export interface InferredItem {
  event_pricing_id: number;
  item_type: number;
  item_type_name: string;
  amount_cents: number;
}

export interface StripeMatchResult {
  charge_id: number | null;
  match_level: number | null;
  amount: number | null;
  is_ambiguous: boolean;
  candidate_charge_ids: number[];
  paid: number | null;
  refunded: number | null;
}

export interface MigrationPreview {
  player_id: number;
  player_name: string;
  paid_amount: number;
  inferred_item_total: number;
  delta: number;
  confidence_score: number;
  confidence_label: string;
  recommended_action: string;
  can_auto_convert: boolean;
  credit_used_cents: number;
  stripe_match: StripeMatchResult | null;
  inferred_items: InferredItem[];
  notes: string;
}

export interface AssessmentSummary {
  total_players: number;
  ready_to_convert: number;
  needs_review: number;
  exact: number;
  high: number;
  medium: number;
  low: number;
  manual_required: number;
}

export interface AssessmentResult {
  is_fully_migrated: boolean;
  summary: AssessmentSummary;
  previews: MigrationPreview[];
  all_pricing: InferredItem[];
  cancelled_players: { player_id: number; player_name: string; audit_id: number }[];
}

export interface AssessmentResponse {
  status: string;
  data: AssessmentResult;
}

export interface ConversionRequest {
  group_event_id: string;
  player_id: string;
  selected_event_pricing_ids: string;
  stripe_charge_id: string;
  inferred_event_pricing_ids: string;
}

export interface ConversionResult {
  registration_payment_id: number;
  already_existed: boolean;
  confidence_score: number;
  source_system: number;
  items_created: number;
}

export interface ConversionResponse {
  status: string;
  data: ConversionResult;
}

// --- Service ---

@Injectable()
export class MigrationService {

  constructor(private http: HttpClient) {}

  /**
   * Calls the assessment endpoint to retrieve migration previews for all
   * unmigrated legacy registrations in the given event.
   */
  assessLegacyRegistrations(groupEventId: string): Observable<AssessmentResponse> {
    const payload = { group_event_id: groupEventId };
    const url = API_URL + 'assessLegacyRegistrations';

    return this.http
      .post<any>(url, payload, { params: { api_key: GGK_API_KEY } })
      .pipe(
        map((resp: any) => {
          // ASMX wraps response in { d: "{\"response\":{\"status\":\"success\",\"data\":{...}}}" }
          const parsed = resp.d
            ? (typeof resp.d === 'string' ? JSON.parse(resp.d) : resp.d)
            : resp;
          return parsed.response as AssessmentResponse;
        })
      );
  }

  /**
   * Calls the conversion endpoint to convert a single legacy registration
   * into the new registration_payment schema.
   */
  convertLegacyRegistration(request: ConversionRequest): Observable<ConversionResponse> {
    const payload = {
      group_event_id: request.group_event_id,
      player_id: request.player_id,
      selected_event_pricing_ids: request.selected_event_pricing_ids,
      stripe_charge_id: request.stripe_charge_id,
      inferred_event_pricing_ids: request.inferred_event_pricing_ids
    };
    const url = API_URL + 'convertLegacyRegistration';

    return this.http
      .post<any>(url, payload, { params: { api_key: GGK_API_KEY } })
      .pipe(
        map((resp: any) => {
          // ASMX wraps response in { d: "{\"response\":{\"status\":\"success\",\"data\":{...}}}" }
          const parsed = resp.d
            ? (typeof resp.d === 'string' ? JSON.parse(resp.d) : resp.d)
            : resp;
          return parsed.response as ConversionResponse;
        })
      );
  }

  /**
   * Remove a cancelled legacy registration audit record.
   */
  removeCancelledRegistration(auditId: number): Observable<ConversionResponse> {
    const url = API_URL + 'removeCancelledLegacyRegistration';
    return this.http
      .post<any>(url, { audit_id: auditId }, { params: { api_key: GGK_API_KEY } })
      .pipe(
        map((resp: any) => {
          const parsed = resp.d
            ? (typeof resp.d === 'string' ? JSON.parse(resp.d) : resp.d)
            : resp;
          return parsed.response as ConversionResponse;
        })
      );
  }
}
